import { useState, useEffect } from 'react'
import { IAPIScenario, IAPIPromptVariant, IExecutionResult } from 'extra/types/ApiModels'
import { TChatHistory } from 'extra/types/Messages'
import { api, HTTPError } from '../../api/client'
import { JSONHelper } from '../../../../extra/Helpers/JSONHelper'
import { ChatHistoryEditor } from '../../components/ChatHistoryEditor'
import { JsonCell, ObjCell, ChatHistoryComponent } from '../../components/DataDisplayComponents'
import { X } from 'lucide-react'

interface ScenarioPanelProps {
  promptId: string
  selectedVariant: IAPIPromptVariant | null
}

function ScenarioMetrics ({ lastOutput }: { lastOutput: IExecutionResult }) {
  const metrics = lastOutput.metrics
  if (!metrics) return null

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 mt-2">
      {metrics.model && <span>Model: <span className="text-gray-200">{metrics.model}</span></span>}
      {metrics.latency != null && <span>Latency: <span className="text-gray-200">{(metrics.latency / 1000).toFixed(2)}s</span></span>}
      {metrics.inputTokens != null && <span>In: <span className="text-gray-200">{metrics.inputTokens}</span></span>}
      {metrics.outputTokens != null && <span>Out: <span className="text-gray-200">{metrics.outputTokens}</span></span>}
      {metrics.thinkingTokens != null && <span>Thinking: <span className="text-gray-200">{metrics.thinkingTokens}</span></span>}
    </div>
  )
}

function ScenarioErrorDisplay ({ lastOutput }: { lastOutput: IExecutionResult }) {
  const error = lastOutput.error
  if (!error) return null

  return (
    <div className="bg-red-900/30 border border-red-700 rounded p-3 text-xs mt-2">
      <div className="font-semibold text-red-300 mb-1">
        Error{error.status ? ` (${error.status} ${error.statusText || ''})` : ''}
      </div>
      <div className="text-red-200">{error.message || JSON.stringify(error)}</div>
      {error.responseBody && (
        <pre className="mt-2 text-red-200/70 whitespace-pre-wrap break-all max-h-32 overflow-auto">
          {typeof error.responseBody === 'string' ? error.responseBody : JSON.stringify(error.responseBody, null, 2)}
        </pre>
      )}
    </div>
  )
}

function ScenarioDetailModal ({ lastOutput, onClose }: { lastOutput: IExecutionResult, onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-[90vw] max-h-[85vh] overflow-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Scenario Execution Details</h3>
          <button
            onClick={onClose}
            className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          {lastOutput.error && (
            <div>
              <label className="input-label mb-1 text-red-400">Error</label>
              <JsonCell data={lastOutput.error} expand />
            </div>
          )}

          {lastOutput.metrics && (
            <div>
              <label className="input-label mb-1">Metrics</label>
              <ObjCell data={lastOutput.metrics} expand />
            </div>
          )}

          {lastOutput.output && (
            <div>
              <label className="input-label mb-1">Output</label>
              <ChatHistoryComponent history={[lastOutput.output] as TChatHistory} expand />
            </div>
          )}

          {lastOutput.request && (
            <div>
              <label className="input-label mb-1">Request</label>
              <JsonCell data={lastOutput.request} expand />
            </div>
          )}

          {lastOutput.response && (
            <div>
              <label className="input-label mb-1">Response</label>
              <JsonCell data={lastOutput.response} expand />
            </div>
          )}

          {lastOutput.rawRequest && (
            <div>
              <label className="input-label mb-1">Raw Request</label>
              <JsonCell data={lastOutput.rawRequest} expand />
            </div>
          )}

          {lastOutput.rawResponse && (
            <div>
              <label className="input-label mb-1">Raw Response</label>
              <JsonCell data={lastOutput.rawResponse} expand />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function ScenarioPanel ({ promptId, selectedVariant }: ScenarioPanelProps) {
  const [scenarios, setScenarios] = useState<Array<IAPIScenario>>([])
  const [scenariosLoading, setScenariosLoading] = useState(true)
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null)
  const [editScenarioTitle, setEditScenarioTitle] = useState('')
  const [editScenarioVariables, setEditScenarioVariables] = useState('')
  const [editScenarioChatHistory, setEditScenarioChatHistory] = useState<TChatHistory>([])
  const [savingScenario, setSavingScenario] = useState(false)
  const [executingScenario, setExecutingScenario] = useState(false)
  const [scenarioError, setScenarioError] = useState<string | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => {
    async function fetchScenarios () {
      try {
        setScenariosLoading(true)
        const scenariosData = await api.scenarios.list(promptId)
        setScenarios(scenariosData)

        if (scenariosData.length > 0) {
          setSelectedScenarioId(scenariosData[0].id)
        }
      } catch (err) {
        setScenarioError(err instanceof Error ? err.message : 'Failed to load scenarios')
      } finally {
        setScenariosLoading(false)
      }
    }

    fetchScenarios()
  }, [promptId])

  const selectedScenario = scenarios.find(s => s.id === selectedScenarioId) || null

  useEffect(() => {
    if (selectedScenario) {
      setEditScenarioTitle(selectedScenario.title || '')
      setEditScenarioVariables(JSONHelper.unescapeNewlinesDeep(JSON.stringify(selectedScenario.variablesValues, null, 2)) || '{}')
      setEditScenarioChatHistory(selectedScenario.chatHistory || [])
      setScenarioError(null)
    }
  }, [selectedScenario?.id])

  const handleSaveScenario = async (): Promise<IAPIScenario | null> => {
    if (!selectedScenario) return null
    setSavingScenario(true)
    setScenarioError(null)
    try {
      let variablesValues = null
      try {
        variablesValues = JSON.parse(JSONHelper.escapeNewlinesInsideJsonStrings(editScenarioVariables))
      } catch {
        setScenarioError('Invalid JSON for Variables')
        setSavingScenario(false)
        return null
      }

      const updated = await api.scenarios.update(selectedScenario.id, {
        title: editScenarioTitle,
        chatHistory: editScenarioChatHistory,
        variablesValues,
      })
      setScenarios(prev => prev.map(s => s.id === updated.id ? updated : s))
      return updated
    } catch (err) {
      setScenarioError(err instanceof Error ? err.message : 'Error saving scenario')
      return null
    } finally {
      setSavingScenario(false)
    }
  }

  const handleCreateScenario = async () => {
    setSavingScenario(true)
    try {
      const newScenario = await api.scenarios.create({
        promptId,
        title: selectedScenario ? `${selectedScenario.title || 'Scenario'} (copy)` : 'New Scenario',
        variablesValues: selectedScenario?.variablesValues || {},
        chatHistory: selectedScenario?.chatHistory || [],
      })
      setScenarios(prev => [...prev, newScenario])
      setSelectedScenarioId(newScenario.id)
    } catch (err) {
      setScenarioError(err instanceof Error ? err.message : 'Error creating scenario')
    } finally {
      setSavingScenario(false)
    }
  }

  const handleExecuteScenario = async () => {
    if (!selectedScenario || !selectedVariant) return

    const saved = await handleSaveScenario()
    if (!saved) return

    setExecutingScenario(true)
    try {
      const updated = await api.scenarios.execute(selectedScenario.id, selectedVariant.id)
      setScenarios(prev => prev.map(s => s.id === updated.id ? updated : s))
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 400) {
        try {
          const body = await err.response.json() as { error: string, details: any, data?: IAPIScenario }
          if (body.data) {
            setScenarios(prev => prev.map(s => s.id === body.data!.id ? body.data! : s))
          }
          setScenarioError(body.error || 'Execution failed')
        } catch {
          setScenarioError('Execution failed')
        }
      } else {
        setScenarioError(err instanceof Error ? err.message : 'Error executing scenario')
      }
    } finally {
      setExecutingScenario(false)
    }
  }

  const handleDeleteScenario = async () => {
    if (!selectedScenario) return
    if (!window.confirm('Delete scenario?')) return
    setSavingScenario(true)
    try {
      await api.scenarios.delete(selectedScenario.id)
      const remaining = scenarios.filter(s => s.id !== selectedScenario.id)
      setScenarios(remaining)
      setSelectedScenarioId(remaining.length > 0 ? remaining[0].id : null)
    } catch (err) {
      setScenarioError(err instanceof Error ? err.message : 'Error deleting scenario')
    } finally {
      setSavingScenario(false)
    }
  }

  return (
    <div className="border-l border-gray-700 pl-6 h-full overflow-auto">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Scenarios</h3>
      </div>

      {scenariosLoading
        ? (
        <div className="text-gray-400 text-sm">Loading scenarios...</div>
          )
        : <>
        <div className="flex gap-2 mb-4">
          <select
            value={selectedScenarioId || ''}
            onChange={e => setSelectedScenarioId(e.target.value || null)}
            className="input flex-1"
          >
            {scenarios.length === 0
              ? <option value="">No scenarios</option>
              : scenarios.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <button onClick={handleCreateScenario} className="btn btn-secondary whitespace-nowrap">+ New</button>
        </div>

        {scenarioError && <div className="alert-error mb-4">{scenarioError}</div>}

        {selectedScenario
          ? (
          <div className="space-y-4">
            <div>
              <label className="input-label">Title</label>
              <input
                type="text"
                className="input"
                value={editScenarioTitle}
                onChange={e => setEditScenarioTitle(e.target.value)}
              />
            </div>

            <div>
              <label className="input-label">Variables (JSON)</label>
              <textarea
                className="input font-mono text-xs"
                rows={4}
                value={editScenarioVariables}
                onChange={e => setEditScenarioVariables(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="input-label">Chat History</label>
              <ChatHistoryEditor
                history={editScenarioChatHistory}
                onChange={setEditScenarioChatHistory}
                defaultRole="user"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSaveScenario}
                disabled={savingScenario}
                className="btn btn-primary"
              >
                {savingScenario ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleExecuteScenario}
                disabled={executingScenario || !selectedVariant}
                className="btn btn-primary bg-green-600 hover:bg-green-700 border-green-600"
              >
                {executingScenario ? 'Executing...' : 'Execute'}
              </button>
              <div className="grow"/>
              <button
                onClick={handleDeleteScenario}
                disabled={savingScenario}
                className="btn btn-danger-light"
              >
                Delete
              </button>
            </div>

            {selectedScenario.lastOutput && (
              <div className="mt-4 border-t border-gray-700 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="input-label">Last Output</label>
                  <button
                    onClick={() => setShowDetail(true)}
                    className="btn btn-secondary text-xs py-1 px-2"
                  >
                    Details
                  </button>
                </div>

                {selectedScenario.lastOutput.error
                  ? <ScenarioErrorDisplay lastOutput={selectedScenario.lastOutput} />
                  : <div className="bg-gray-900 p-3 rounded text-xs font-mono whitespace-pre-wrap text-gray-300">
                      {typeof selectedScenario.lastOutput.output?.content === 'string'
                        ? selectedScenario.lastOutput.output.content
                        : JSON.stringify(selectedScenario.lastOutput.output, null, 2)}
                    </div>
                }

                <ScenarioMetrics lastOutput={selectedScenario.lastOutput} />

                {showDetail && (
                  <ScenarioDetailModal
                    lastOutput={selectedScenario.lastOutput}
                    onClose={() => setShowDetail(false)}
                  />
                )}
              </div>
            )}
          </div>
            )
          : (
          <div className="text-gray-400">Select or create a scenario</div>
            )}
      </>}
    </div>
  )
}
